import { createFileRoute, redirect } from "@tanstack/react-router";
import { UserLoginForm } from "@/components/userLoginForm";
import { resolvePostAuthDestination } from "@/lib/auth/postAuthDestination";
import { ensureAuthenticatedSession } from "@/lib/auth/sessionBootstrap";
import { useEffect } from "react";
import { getOfferFromSearch, persistOffer } from "@/lib/billing/offers";

const resolveSafeNextPath = (next: string | null | undefined): string | null => {
  if (!next) return null;
  if (!next.startsWith("/")) return null;
  if (next.startsWith("//")) return null;
  return next;
};

export const Route = createFileRoute("/(unauth)/login")({
  component: LoginPage,
  async beforeLoad({ context, location }) {
    if (context.session) {
      await ensureAuthenticatedSession(context.session.user, { staleOk: true });

      const parsed = new URLSearchParams(location.searchStr);
      const safeNext = resolveSafeNextPath(parsed.get("next"));
      if (safeNext) {
        throw redirect({ href: safeNext, replace: true });
      }

      const destination = await resolvePostAuthDestination(context.session.user.id);
      throw redirect({ to: destination, replace: true });
    }
  },
});

function LoginPage() {
  useEffect(() => {
    const loginParams = new URLSearchParams(window.location.search);
    const next = resolveSafeNextPath(loginParams.get("next"));
    let offer = getOfferFromSearch(window.location.search);
    if (!offer) {
      if (next) {
        const nextSearch = next.includes("?")
          ? next.slice(next.indexOf("?"))
          : "";
        offer = getOfferFromSearch(nextSearch);
      }
    }
    if (offer) {
      persistOffer(offer);
    }
  }, []);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-slate-50 p-6 md:p-10">
      <div className="flex w-full max-w-md flex-col gap-6">
        <a href="#" className="flex items-center gap-2 self-center font-medium">
          <img
            src="/lockupBlack.svg"
            alt="Trainrr"
            className="h-6 [filter:brightness(0)_saturate(100%)_invert(20%)_sepia(98%)_saturate(3828%)_hue-rotate(219deg)_brightness(103%)_contrast(104%)]"
          />
        </a>
        <UserLoginForm />
        <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4">
          By clicking continue, you agree to our{" "}
          <a className="hover:text-primary" href="https://trainrr.app/terms">
            Terms of Service
          </a>{" "}
          and{" "}
          <a className="hover:text-primary" href="https://trainrr.app/privacy">
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </div>
  );
}
